/**
 * Consent configuration for the c15t consent manager.
 *
 * Background: a California arbitration claim requires that no non-essential tracker fires a
 * network request before the user makes a choice (see the "Web Privacy/Tracking Consent brief").
 * c15t gives us true prior blocking: it gates scripts and intercepts fetch/XHR, keeps categories
 * denied by default, detects the GPC signal, and (with a backend) stores a server-side consent log.
 *
 * APP-1182 carves out one exception counsel signed off on: GA4 may run before the choice, but only
 * in Google's cookieless mode. That is why the Google domains are absent from the block list below.
 *
 * The brief mandates exactly three categories. We map them onto c15t's built-in category names:
 *   necessary     -> "Essential"  first-party analytics to our own backend + session (locked on)
 *   functionality -> "Functional" (reserved; no vendor gated on it yet)
 *   measurement   -> "Tracking"   GTM/GA, AppsFlyer, Sentry Session Replay
 *
 * Every third-party tracker in the brief's block list is gated on the single "Tracking" category.
 */

import type { AllConsentNames, ConsentManagerOptions } from "@c15t/react";
import { isProduction } from "~/lib/env";

/** The three categories shown in the banner (brief R4). Order controls display order. */
export const CONSENT_CATEGORIES: AllConsentNames[] = ["necessary", "functionality", "measurement"];

/** The category that gates every third-party tracker in the brief's block list. */
export const TRACKING: AllConsentNames = "measurement";

/**
 * Versions recorded with every consent event (brief section 5). Bump `NOTICE_VERSION` whenever the
 * banner copy changes and `VENDOR_LIST_VERSION` whenever the tracker list below changes — a log that
 * points to a version we cannot reproduce is not evidence.
 *
 * `NOTICE_VERSION` reaches the c15t consent record through {@link BANNER_UI_SOURCE} and
 * {@link DIALOG_UI_SOURCE}; the privacy-policy version is pinned server-side by the policy snapshot
 * c15t attaches to every record.
 */
export const NOTICE_VERSION = "v2.1";
export const VENDOR_LIST_VERSION = "v2026-09-02";

/**
 * The self-hosted c15t backend. Offline mode keeps the choice in the browser only and gives no audit
 * evidence (brief section 3), so it is used solely for local dev when no backend is configured.
 */
const backendURL = import.meta.env.VITE_C15T_BACKEND_URL as string | undefined;

if (isProduction && !backendURL && typeof window !== "undefined") {
  // ponytail: prior blocking (R1/R3) still works offline; only the server-side consent log (R7) is
  // lost. Fail loud rather than crash the site, and require the backend URL before a real prod deploy.
  console.error(
    "[consent] VITE_C15T_BACKEND_URL is not set in production. Consent audit logging (brief R7) is DISABLED."
  );
}

/**
 * The `uiSource` recorded with a consent. c15t sends it verbatim with the consent record but offers
 * no metadata hook on its save path, so tagging it with the notice version is what ties a stored
 * consent back to the exact copy the user was shown.
 *
 * Both surfaces need one. A consent saved from the preferences dialog is recorded with the dialog's
 * own default `uiSource` unless it is overridden too, and would carry no version at all.
 */
export const BANNER_UI_SOURCE = `banner@${NOTICE_VERSION}`;
export const DIALOG_UI_SOURCE = `dialog@${NOTICE_VERSION}`;

/**
 * Backstop for prior blocking. c15t intercepts global fetch/XHR and blocks these tracker domains
 * until Tracking consent is granted, even if a vendor SDK slips past the code-level script gate.
 * (This does not block `<script src>` loads — those are gated in the vendor loader.)
 *
 * The Google domains are deliberately NOT listed: APP-1182 requires GA4 to run cookieless before the
 * choice, and blocking googletagmanager.com would stop the very load that enables it.
 */
const TRACKER_DOMAINS = ["appsflyer.com"];

/**
 * Copy overrides. Counsel must sign off on the final banner text and button labels before release
 * (brief section 8) — these are sensible defaults, not the approved copy.
 */
const messages = {
  en: {
    cookieBanner: {
      title: "Your privacy choices",
      description:
        "We use essential cookies to run Blidz, and — with your permission — cookies to improve the site and measure performance. Nothing non-essential runs until you choose.",
    },
    consentManagerDialog: {
      title: "Manage your privacy choices",
      description: "Choose which categories of cookies and tracking you allow. Essential is always on.",
    },
    consentTypes: {
      necessary: {
        title: "Essential",
        description: "Required for the site to work: sign-in, cart and security. Always on.",
      },
      functionality: {
        title: "Functional",
        description: "Optional features that remember your preferences.",
      },
      measurement: {
        title: "Tracking",
        description: "Analytics and marketing measurement (Google, AppsFlyer, session replay).",
      },
    },
  },
};

/**
 * c15t ships its own blue. Point its tokens at the app's by reference rather than by value: CSS
 * custom properties nest, so `var(--primary)` already resolves per theme and neither colour has to
 * be restated here. Only the primary family is overridden — c15t's neutral surface and text match
 * closely enough to leave alone.
 *
 * The same object is used for both modes on purpose. c15t emits a separate dark block and falls back
 * to its own dark palette for any key `dark` omits, so leaving it unset puts the blue back under
 * `.dark` — while these `var()`s need no per-mode variant to begin with.
 */
const primaryTokens = {
  primary: "var(--primary)",
  // Mirrors `hover:bg-primary/90` on the app's own buttons.
  primaryHover: "color-mix(in srgb, var(--primary) 90%, transparent)",
  textOnPrimary: "var(--primary-foreground)",
  switchTrackActive: "var(--primary)",
};

/**
 * c15t already animates the banner in from 50px below on a spring curve, but defaults the duration
 * to 80ms — far too fast for the overshoot to register. Slowing it is what draws the eye now that
 * there is no overlay to do that job. Tailwind's `transition-duration` sits in the utilities layer,
 * so it overrides the duration inside the library's `@layer components` transition shorthand while
 * leaving its easing intact.
 */
const theme = {
  colors: primaryTokens,
  dark: primaryTokens,
  // Accept is the filled action on both surfaces. Expressed through c15t's own API rather than a
  // className on the banner's button, because the dialog's copy of that button takes no props from
  // us — one declaration covers both, and `variant: "primary"` resolves through the tokens above.
  consentActions: {
    // Save has no key of its own and falls to `default`; neutral is what drops the primary-coloured
    // inset ring it would otherwise draw, leaving Accept as the only filled action.
    default: { variant: "neutral", mode: "stroke" },
    accept: { variant: "primary", mode: "filled" },
  },
  slots: {
    // Mobile only: pin the banner to the top so it never covers the sticky "Buy now" bar, which is
    // fixed to the bottom on <lg (deal/detail). On lg+ the buy bar is static, so the banner keeps
    // c15t's default bottom placement. Tailwind utilities win over c15t's @layer components position.
    consentBanner: "duration-500 p-1 max-lg:top-0 max-lg:bottom-auto",
    // Full-width on mobile (drop the max-w cap), and no card padding — its header/footer bring their
    // own spacing. Keep the max-w-md cap on desktop so the bottom banner is not full-bleed there.
    consentBannerCard: "relative w-full max-w-md max-lg:max-w-none p-0",
    // Trim the header/footer inner padding (c15t defaults are 16px / 16px 20px) and tighten the
    // title→description gap, so the card packs into a slim bar. Buttons keep their own 8/12 padding.
    consentBannerHeader: "px-3 py-2",
    consentBannerDescription: "text-xs leading-tight mt-1",
    // Put all three actions on one row instead of stacking Customize above Reject/Accept — saves a row.
    consentBannerFooter: "flex-row flex-wrap items-center gap-2 px-3 py-2",
    consentBannerFooterSubGroup: "flex-1 gap-2",
  },
} satisfies ConsentManagerOptions["theme"];

const shared = {
  consentCategories: CONSENT_CATEGORIES,
  networkBlocker: {
    rules: TRACKER_DOMAINS.map(domain => ({ domain, category: TRACKING })),
  },
  i18n: { locale: "en", messages },
  theme,
} satisfies Partial<ConsentManagerOptions>;

export const consentOptions: ConsentManagerOptions = backendURL
  ? { mode: "hosted", backendURL, ...shared }
  : { mode: "offline", ...shared };
